import React, { useEffect, useState } from 'react';
import { Search, Users, FileText, BarChart3, Activity, StickyNote, ChevronLeft, ChevronRight } from 'lucide-react';
import { getAllUsers } from '../../../../services/userService';
import { getPerformanceResponses } from '../../../../services/performanceService';
import { getDailyQuestionResponses } from '../../../../services/dailyQuestionService';
import './UserList.css';

const UserList = ({ onUserClick, onOverviewClick, onNoteClick, onBodyMetricsClick, user }) => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [groupFilter, setGroupFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const usersPerPage = 10;
    const cutoffDate = new Date('2025-10-01');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await getAllUsers();
                const usersWithActivity = await Promise.all(res.map(async (restUser) => {
                    if (restUser._id !== user._id) {
                        let lastPerformanceDate = null;
                        let lastDailyQuestionDate = null;
                        let lastActive = "Initial survey not completed yet or no daily/hourly records";

                        try {
                            const performanceData = await getPerformanceResponses(restUser._id);
                            if (performanceData && performanceData.data && performanceData.data.length > 0) {
                                lastPerformanceDate = new Date(
                                    Math.max(
                                        ...performanceData.data.map(r => new Date(r.timestamp).getTime())
                                    )
                                );
                            }
                        } catch (error) {
                            if (error.response && error.response.status === 404) {
                                console.warn(`No performance data for user ${restUser._id}`);
                            } else {
                                throw error;
                            }
                        }

                        try {
                            const dailyQuestionData = await getDailyQuestionResponses(restUser._id);
                            if (dailyQuestionData?.data.length > 0) {
                                lastDailyQuestionDate = new Date(
                                    Math.max(
                                        ...dailyQuestionData.data.map(r => new Date(r.date).getTime())
                                    )
                                );
                            }
                        } catch (error) {
                            if (error.response && error.response.status === 404) {
                                console.warn(`No daily question data for user ${restUser._id}`);
                            } else {
                                throw error;
                            }
                        }

                        const lastActiveDate = [lastPerformanceDate, lastDailyQuestionDate].filter(Boolean).sort((a, b) => b - a)[0];
                        if (lastActiveDate) {
                            lastActive = lastActiveDate;
                        }

                        return { ...restUser, lastActive };
                    }
                    return null;
                }));

                const filteredUsers = usersWithActivity.filter(Boolean);
                setUsers(filteredUsers);
                setFilteredUsers(filteredUsers);
            } catch (error) {
                console.error("Error fetching users or activity data:", error);
                setUsers([]);
                setFilteredUsers([]);
            }
        };

        fetchData();
    }, [user._id]);

    useEffect(() => {
        let results = users.filter(
            u =>
                u.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                u.lastname.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Apply group filter
        if (groupFilter !== 'all') {
            results = results.filter(u => {
                if (groupFilter === 'control') {
                    return u.studyGroup === 'control';
                } else if (groupFilter === 'experimental') {
                    return u.studyGroup === 'experimental';
                } else if (groupFilter === 'unassigned') {
                    // Match the Group Assignment logic - only show unassigned users registered after cutoff date
                    const isUnassigned = !u.studyGroup || u.studyGroup === 'unassigned' || u.studyGroup === '';
                    const userCreatedAt = new Date(u.createdAt);
                    const isAfterCutoff = userCreatedAt >= cutoffDate;
                    return isUnassigned && isAfterCutoff;
                }
                return true;
            });
        }

        setFilteredUsers(results);
        setCurrentPage(1);
    }, [searchTerm, users, groupFilter]);

    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

    const handleNextPage = () => {
        setCurrentPage(prevPage => Math.min(prevPage + 1, Math.ceil(filteredUsers.length / usersPerPage)));
    };

    const handlePreviousPage = () => {
        setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
    };

    const getStatusClass = (lastActive) => {
        if (typeof lastActive === 'string') return 'status-inactive';

        const daysSinceActive = Math.floor((new Date() - new Date(lastActive)) / (1000 * 60 * 60 * 24));
        if (daysSinceActive <= 1) return 'status-active';
        if (daysSinceActive <= 7) return 'status-moderate';
        return 'status-inactive';
    };

    const getGroupBadge = (studyGroup) => {
        if (!studyGroup || studyGroup === 'unassigned' || studyGroup === '') {
            return <span className="group-badge unassigned">Unassigned</span>;
        } else if (studyGroup === 'control') {
            return <span className="group-badge control">Control</span>;
        } else if (studyGroup === 'experimental') {
            return <span className="group-badge experimental">Experimental</span>;
        }
        return <span className="group-badge">{studyGroup}</span>;
    };

    return (
        <div className="user-list-wrapper">
            <div className="user-list-container modern">
                <div className="user-list-header">
                    <h2 className="user-list-title">
                        <Users className="header-icon" />
                        Participants List ({filteredUsers.length})
                    </h2>
                </div>

                <div className="filter-container">
                    <div className="group-filter-buttons">
                        <button
                            className={`filter-btn ${groupFilter === 'all' ? 'active' : ''}`}
                            onClick={() => setGroupFilter('all')}
                        >
                            All
                        </button>
                        <button
                            className={`filter-btn ${groupFilter === 'control' ? 'active' : ''}`}
                            onClick={() => setGroupFilter('control')}
                        >
                            Control
                        </button>
                        <button
                            className={`filter-btn ${groupFilter === 'experimental' ? 'active' : ''}`}
                            onClick={() => setGroupFilter('experimental')}
                        >
                            Experimental
                        </button>
                        <button
                            className={`filter-btn ${groupFilter === 'unassigned' ? 'active' : ''}`}
                            onClick={() => setGroupFilter('unassigned')}
                            title="Users registered after Oct 1, 2025 without group assignment"
                        >
                            Unassigned
                        </button>
                    </div>
                </div>

                <div className="search-container">
                    <div className="search-wrapper">
                        <Search className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by first name or last name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-bar modern"
                        />
                    </div>
                </div>

                <div className="table-container">
                    <table className="modern-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Study Group</th>
                                <th>Date Added</th>
                                <th>Last Active</th>
                                <th className="actions-column">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentUsers.map((user, index) => (
                                <tr key={user._id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                    <td>
                                        <div className="user-info">
                                            <div className="user-avatar">
                                                {user.firstname[0]}{user.lastname[0]}
                                            </div>
                                            <div className="user-name">
                                                {user.firstname} {user.lastname}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="email-cell">{user.email}</td>
                                    <td>{getGroupBadge(user.studyGroup)}</td>
                                    <td className="date-cell">
                                        {new Date(user.createdAt).toLocaleDateString()}
                                    </td>
                                    <td>
                                        <span className={`status-badge ${getStatusClass(user.lastActive)}`}>
                                            {typeof user.lastActive === 'string'
                                                ? 'Not Active'
                                                : new Date(user.lastActive).toLocaleDateString()}
                                        </span>
                                    </td>
                                    <td className="actions-cell">
                                        <div className="action-buttons">
                                            <button 
                                                onClick={() => onUserClick(user._id)}
                                                className="action-btn survey-btn"
                                                title="View Survey"
                                            >
                                                <FileText className="btn-icon" />
                                                <span className="btn-text">Survey</span>
                                            </button>
                                            <button 
                                                onClick={() => onOverviewClick(user._id)}
                                                className="action-btn stats-btn"
                                                title="View Statistics"
                                            >
                                                <BarChart3 className="btn-icon" />
                                                <span className="btn-text">Statistics</span>
                                            </button>
                                            <button 
                                                onClick={() => onBodyMetricsClick(user._id)}
                                                className="action-btn metrics-btn"
                                                title="View Body Metrics"
                                            >
                                                <Activity className="btn-icon" />
                                                <span className="btn-text">Metrics</span>
                                            </button>
                                            <button 
                                                onClick={() => onNoteClick(user._id)}
                                                className="action-btn notes-btn"
                                                title="Add Notes"
                                            >
                                                <StickyNote className="btn-icon" />
                                                <span className="btn-text">Notes</span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="pagination modern">
                    <button 
                        onClick={handlePreviousPage} 
                        disabled={currentPage === 1}
                        className="pagination-btn"
                    >
                        <ChevronLeft className="chevron-icon" />
                        Previous
                    </button>
                    <span className="page-info">
                        Page {currentPage} of {Math.ceil(filteredUsers.length / usersPerPage)}
                    </span>
                    <button 
                        onClick={handleNextPage} 
                        disabled={currentPage === Math.ceil(filteredUsers.length / usersPerPage)}
                        className="pagination-btn"
                    >
                        Next
                        <ChevronRight className="chevron-icon" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserList;